import { useState, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import * as authAPI from '@/api/auth';
import Input from '@/ui/inputs/Input';
import Checkbox from '@/ui/checkbox/defaultCheckbox/CheckBox';
import { Button } from '@/components/ui/button';
import { Check } from 'lucide-react';
import eye from '@/assets/eye.svg';
import eyeSlash from '@/assets/eye_slash.svg';
import { Eye } from './Signup.styles';
import * as amplitude from '@amplitude/analytics-browser';
import { Dialog, DialogPanel, DialogTitle, Transition, TransitionChild } from '@headlessui/react';
import { toast } from 'sonner';

export const SignupContent = ({ onSignin }: { onSignin: () => void }) => {
  const [t] = useTranslation('signUp');

  const [openModal, setOpenModal] = useState(false);
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [emailVerification, setEmailVerification] = useState(null);
  const [password, setPassword] = useState('');
  const [passwordCheck, setPasswordCheck] = useState('');
  const navigate = useNavigate();
  const [validationState, setValidationState] = useState<{
    letter: boolean;
    number: boolean;
    characters: boolean;
    email?: boolean | null;
  }>({
    letter: false,
    number: false,
    characters: false,
    email: null,
  });
  const [checklist, setChecklist] = useState([false, false, false]);
  const [showPassword, setShowPassword] = useState(false);
  const [nameErrorMsg, setNameErrorMsg] = useState(null);
  const [passwordErrorMsg, setPasswordErrorMsg] = useState(null);
  const [checklistErorrMsg, setChecklistErrorMsg] = useState(null);

  const checkBox = (index) => {
    let arr = checklist.slice();
    if (index === 0) {
      arr = arr.map((el) => !checklist[0]);
    } else {
      arr[index] = !arr[index];
    }
    setChecklist(arr);
  };

  const handleInput = (target, e) => {
    if (target === 'fullName') {
      setFullName(e.target.value);
    }
    if (target === 'email') {
      setEmailVerification(null);
      setEmail(e.target.value);
    }
    if (target === 'password') {
      setPassword(e.target.value);
      const checkResults = passwordValidationCheck(e.target.value);
      setValidationState({
        letter: checkResults.hasLetter,
        number: checkResults.hasNumber,
        characters: checkResults.has816Characters,
      });
    }
    if (target === 'passwordCheck') {
      setPasswordCheck(e.target.value);
    }
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const passwordValidationCheck = (string) => {
    let hasLetter = false;
    let hasNumber = false;
    let has816Characters = false;

    if (string.length >= 8 && string.length <= 16) {
      has816Characters = true;
    } else {
      has816Characters = false;
    }

    if (/\d/.test(string)) {
      hasNumber = true;
    } else {
      hasNumber = false;
    }

    if (/[a-zA-Z]/.test(string)) {
      hasLetter = true;
    } else {
      hasLetter = false;
    }
    return { hasLetter: hasLetter, hasNumber: hasNumber, has816Characters: has816Characters };
  };

  const emailValidationCheck = async () => {
    if (!email) {
      return toast.error(t('Please enter your email.'));
    }
    const emailRule =
      /^[0-9a-zA-Z]([-_.]?[0-9a-zA-Z])*@[0-9a-zA-Z]([-_.]?[0-9a-zA-Z])*.[a-zA-Z]{2,3}$/i;
    let arr = Object.assign({}, validationState);

    const result = await authAPI.checkEmail(email);
    if (result?.status === 200) {
      if (result.data.exist && emailRule.test(email)) {
        arr.email = true;
        setEmailVerification('This email account already exists.');
      }
      if (!result.data.exist && emailRule.test(email)) {
        arr.email = false;
        setEmailVerification('verified');
      }
      if (!result.data.exist && !emailRule.test(email)) {
        arr.email = false;
        setEmailVerification('This is not a correct email.');
      }
    }
  };

  const handleRegistration = async () => {
    if (fullName.length >= 257) {
      setNameErrorMsg('Name is too long.');
    } else if (fullName.length === 0) {
      setNameErrorMsg('Please enter your name.');
    } else {
      setNameErrorMsg(null);
    }

    if (password !== passwordCheck) {
      setPasswordErrorMsg('Passwords do not match.');
    } else if (!validationState.characters || !validationState.letter || !validationState.number)
      setPasswordErrorMsg('Password is not correct.');
    else setPasswordErrorMsg(null);

    if (!checklist[1] || !checklist[2])
      setChecklistErrorMsg('Please agree to the terms and conditions.');
    else setChecklistErrorMsg(null);

    if (emailVerification !== 'verified') {
      setEmailVerification('Please verify your email.');
    } else {
      setEmailVerification('verified');
    }

    if (
      fullName.length >= 257 ||
      fullName.length === 0 ||
      password !== passwordCheck ||
      !validationState.characters ||
      !validationState.letter ||
      !validationState.number ||
      emailVerification !== 'verified' ||
      !checklist[1] ||
      !checklist[2]
    )
      return;

    const formData = {
      fullname: fullName,
      email: email,
      password: password,
      password_confirm: passwordCheck,
    };
    const result = await authAPI.signup(formData);
    amplitude.track('signup_complete');
    if (result && result.status === 201) {
      amplitude.track('email_verification_start');
      setOpenModal(true);
    }
  };

  const handleEnter = (e) => {
    if (e.keyCode === 13) {
      handleRegistration();
    }
  };

  const closeModal = () => {
    setOpenModal(false);
    navigate('/signin');
  };

  return (
    <>
      <div className="flex flex-col gap-4">
        <div className="flex flex-col items-center gap-2 text-center">
          <h1 className="text-2xl font-bold">{t('Create an account')}</h1>
          <p className="text-muted-foreground text-sm text-balance">
            {t("Let's get started. Fill in the details below to create your account.")}
          </p>
        </div>
        <div className="space-y-2">
          <Input
            value={fullName}
            placeHolder={t('Name')}
            onChange={(e) => handleInput('fullName', e)}
            helperText={nameErrorMsg && t(nameErrorMsg)}
            className="h-11"
          />
          <Input
            value={email}
            placeHolder={t('Email')}
            onChange={(e) => handleInput('email', e)}
            rightComponent={
              <div className="ml-3 cursor-pointer" onClick={emailValidationCheck}>
                {emailVerification === 'verified' ? (
                  <div className="text-primary-500 text-sm w-max">{t('Available')}</div>
                ) : (
                  <div className="text-primary-500 w-max text-sm">{t('Check')}</div>
                )}
              </div>
            }
            helperText={emailVerification !== 'verified' && t(emailVerification)}
            className="h-11"
          />
          <Input
            value={password}
            type={showPassword ? 'text' : 'password'}
            placeHolder={t('Password')}
            onChange={(e) => handleInput('password', e)}
            rightComponent={
              <Eye
                onClick={handleClickShowPassword}
                src={showPassword ? eyeSlash : eye}
                alt="eye"
              />
            }
            className="h-11"
          />
          <Input
            value={passwordCheck}
            type={showPassword ? 'text' : 'password'}
            placeHolder={t('Confirm Password')}
            onChange={(e) => handleInput('passwordCheck', e)}
            rightComponent={
              <Eye
                onClick={handleClickShowPassword}
                src={showPassword ? eyeSlash : eye}
                alt="eye"
              />
            }
            helperText={passwordErrorMsg && t(passwordErrorMsg)}
            className="h-11"
          />
        </div>

        <div className="border rounded-lg px-5 py-3">
          <div className="text-text-400 text-sm mb-2">{t('Your password must contain:')}</div>
          <div className="flex items-center">
            {validationState.characters ? (
              <Check className="w-4 h-4 mr-2 text-main" />
            ) : (
              <span className="mr-2">•</span>
            )}
            <div className="text-text-400 inline-block text-sm">{t('8~16 characters')}</div>
          </div>
          <div className="flex items-center">
            {validationState.number && validationState.letter ? (
              <Check className="w-4 h-4 mr-2 text-main" />
            ) : (
              <span className="mr-2">•</span>
            )}
            <div className="text-text-400 inline-block text-sm">
              {t('both numbers and letters')}
            </div>
          </div>
        </div>

        <div className="border rounded-lg space-y-2 px-5 py-3">
          <div className="flex items-center gap-2">
            <Checkbox isChecked={checklist[0]} onClick={() => checkBox(0)} />
            <div className="text-text-400 text-sm">{t('Agree all')}</div>
          </div>

          {/* <Divider /> */}

          <div className="flex items-center gap-2">
            <Checkbox isChecked={checklist[1]} onClick={() => checkBox(1)} />
            <div className="text-text-400 text-sm">
              {t('[Required] I am 14 years of age or older')}
            </div>
          </div>

          <div className="flex items-start gap-2">
            <div>
              <Checkbox isChecked={checklist[2]} onClick={() => checkBox(2)} />
            </div>
            <div className="flex flex-wrap items-center gap-1">
              <div className="text-text-400 text-sm">{t('[Required] I agree to ')}</div>
              <div
                onClick={() => window.open('/terms', '_blank')}
                style={{ display: 'inline-block', cursor: 'pointer' }}
              >
                <div className="text-text-400 underline text-sm">{t('Term of Use')}</div>
              </div>
              <div className="text-text-400 text-sm">{t(' and ')}</div>
              <div
                onClick={() => window.open('/privacy', '_blank')}
                style={{ display: 'inline-block', cursor: 'pointer' }}
              >
                <div className="text-text-400 underline text-sm">{t('Privacy Policy')}</div>
              </div>
            </div>
          </div>
        </div>
        {checklistErorrMsg && <div className="text-red-200">{t(checklistErorrMsg)}</div>}
        <Button
          onKeyDown={(e) => handleEnter(e)}
          onClick={() => handleRegistration()}
          className="h-11 bg-main w-full"
        >
          <div className="text-sm">{t('Continue')}</div>
        </Button>

        <div className="flex items-center justify-around">
          <div className="text-muted-foreground text-xs">{t('Already have an account?')}</div>
          <Button variant="ghost" onClick={onSignin}>
            <div className="text-main text-sm">{t('Login')}</div>
          </Button>
        </div>
      </div>

      <Transition appear show={openModal} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={closeModal}>
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/25" />
          </TransitionChild>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <TransitionChild
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <DialogPanel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                  <div className="text-md font-medium leading-6 text-gray-900">
                    {t('Registration completed successfully')}
                  </div>
                  <div className="my-4">
                    <div>
                      <div className="text-muted-foreground text-sm">
                        {t('We Appreciate Your Interest!')}
                      </div>
                    </div>
                    <div>
                      <div className="text-muted-foreground text-sm">
                        {t('Please check your mailbox and verify Email account.')}
                      </div>
                    </div>
                  </div>
                </DialogPanel>
              </TransitionChild>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
};

export default SignupContent;
